import React, { useState, useEffect, useCallback } from 'react';
import { 
  Clock, Play, Square, Plus, Calendar, DollarSign, Users, 
  Download, Filter, Edit2, Trash2, ChevronDown, Timer, 
  TrendingUp, FileText, MoreHorizontal, Briefcase
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Switch } from '../ui/switch';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import api from '../../utils/api';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const TimeTracking = () => {
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [summary, setSummary] = useState(null);
  const [allUsersSummary, setAllUsersSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTimer, setActiveTimer] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  
  const [filters, setFilters] = useState({
    client_id: '',
    period: 'week',
    billable: '',
    user_id: ''  // Employee filter - only for super admins
  });

  const [formData, setFormData] = useState({
    client_id: '',
    description: '',
    hours: 0,
    minutes: 0,
    billable: true,
    hourly_rate: '',
    service_id: '',
    date: new Date().toISOString().split('T')[0]
  });

  const isSuperAdmin = user?.role === 'super_admin';

  useEffect(() => {
    fetchData();
  }, [filters]);

  useEffect(() => {
    // Timer interval for active timer display
    let interval;
    if (activeTimer?.timer_running) {
      interval = setInterval(() => {
        const started = new Date(activeTimer.timer_started_at);
        const now = new Date();
        const elapsed = Math.floor((now - started) / 1000);
        setElapsedTime(elapsed);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [activeTimer]);

  const fetchData = async () => {
    try {
      // Build params for time entries - include user_id filter for super admins
      const entriesParams = { 
        client_id: filters.client_id || undefined,
        user_id: (isSuperAdmin && filters.user_id) ? filters.user_id : undefined
      };
      
      const summaryParams = { 
        period: filters.period,
        user_id: (isSuperAdmin && filters.user_id) ? filters.user_id : undefined
      };

      const requests = [
        api.get('/time-entries', { params: entriesParams }),
        api.get('/clients'),
        api.get('/service-packages'),
        api.get('/time-entries/summary', { params: summaryParams }),
        api.get('/time-entries/active-timer')
      ];

      // Super admin: also fetch team members for the employee filter
      if (isSuperAdmin) {
        requests.push(api.get('/team'));
        requests.push(api.get('/time-entries/all-users'));
      }

      const results = await Promise.all(requests);
      
      setEntries(results[0].data);
      setClients(results[1].data);
      setServices(results[2].data);
      setSummary(results[3].data);
      setActiveTimer(results[4].data);

      // Super admin: set employees and all users summary
      if (isSuperAdmin) {
        // Combine current user with team members for employee filter
        const teamMembers = results[5].data || [];
        const allEmployees = [
          { id: user.id, first_name: user.first_name, last_name: user.last_name, role: user.role, email: user.email },
          ...teamMembers.map(m => ({
            id: m.id,
            first_name: m.first_name,
            last_name: m.last_name,
            role: m.role,
            email: m.email
          }))
        ];
        // Remove duplicates by id
        const uniqueEmployees = allEmployees.filter((emp, index, self) => 
          index === self.findIndex(e => e.id === emp.id)
        );
        setEmployees(uniqueEmployees);
        setAllUsersSummary(results[6].data);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load time tracking data');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      client_id: '',
      description: '',
      hours: 0,
      minutes: 0,
      billable: true,
      hourly_rate: '',
      service_id: '',
      date: new Date().toISOString().split('T')[0]
    });
    setEditingEntry(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.client_id) {
      toast.error('Please select a client');
      return;
    }

    try {
      const submitData = {
        ...formData,
        hours: parseFloat(formData.hours) || 0,
        minutes: parseInt(formData.minutes) || 0,
        hourly_rate: formData.hourly_rate ? parseFloat(formData.hourly_rate) : null,
        service_id: formData.service_id || null,
        date: new Date(formData.date).toISOString()
      };

      if (editingEntry) {
        await api.put(`/time-entries/${editingEntry.id}`, submitData);
        toast.success('Time entry updated!');
      } else {
        await api.post('/time-entries', submitData);
        toast.success('Time entry created!');
      }
      
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Failed to save time entry:', error);
      toast.error(error.response?.data?.detail || 'Failed to save time entry');
    }
  };

  const handleStartTimer = async () => {
    if (!formData.client_id) {
      toast.error('Please select a client first');
      return;
    }

    try {
      const response = await api.post('/time-entries/start-timer', null, {
        params: {
          client_id: formData.client_id,
          description: formData.description || 'Working...',
          service_id: formData.service_id || undefined
        }
      });
      setActiveTimer(response.data);
      setElapsedTime(0);
      setDialogOpen(false);
      toast.success('Timer started!');
      fetchData();
    } catch (error) {
      console.error('Failed to start timer:', error);
      toast.error(error.response?.data?.detail || 'Failed to start timer');
    }
  };

  const handleStopTimer = async () => {
    if (!activeTimer) return;

    try {
      const response = await api.post(`/time-entries/${activeTimer.id}/stop-timer`);
      toast.success(`Timer stopped! Logged ${response.data.hours}h ${response.data.minutes}m`);
      setActiveTimer(null);
      setElapsedTime(0);
      fetchData();
    } catch (error) {
      console.error('Failed to stop timer:', error);
      toast.error(error.response?.data?.detail || 'Failed to stop timer');
    }
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm('Are you sure you want to delete this entry?')) return;

    try {
      await api.delete(`/time-entries/${entryId}`);
      toast.success('Time entry deleted');
      fetchData();
    } catch (error) {
      console.error('Failed to delete:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete entry');
    }
  };

  const handleExportCSV = async () => {
    try {
      const response = await api.get('/time-entries/export', {
        params: { client_id: filters.client_id || undefined },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `time_entries_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('CSV exported!');
    } catch (error) {
      console.error('Failed to export:', error);
      toast.error('Failed to export CSV');
    }
  };

  const formatDuration = (hours, minutes) => {
    const h = Math.floor(hours || 0);
    const m = Math.floor(minutes || 0);
    if (h === 0 && m === 0) return '0m';
    if (h === 0) return `${m}m`;
    if (m === 0) return `${h}h`;
    return `${h}h ${m}m`;
  };

  const formatElapsedTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client?.company_name || 'Unknown Client';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header with Active Timer */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-1" data-testid="time-tracking-title">Time Tracking</h2>
          <p className="text-gray-600">Track billable and non-billable hours per client</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {activeTimer ? (
            <div className="flex items-center gap-3 bg-red-50 border border-red-200 px-4 py-2 rounded-lg">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                <span className="text-red-700 font-medium">{activeTimer.client_name}</span>
              </div>
              <span className="font-mono text-lg text-red-700">{formatElapsedTime(elapsedTime)}</span>
              <Button
                size="sm"
                onClick={handleStopTimer}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="stop-timer-button"
              >
                <Square className="w-4 h-4 mr-1" />
                Stop
              </Button>
            </div>
          ) : (
            <Button
              onClick={() => { resetForm(); setDialogOpen(true); }}
              className="bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white"
              data-testid="new-entry-button"
            >
              <Plus className="w-4 h-4 mr-2" />
              Log Time
            </Button>
          )}
          <Button variant="outline" onClick={handleExportCSV} data-testid="export-csv-button">
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="glass rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">This {filters.period === 'week' ? 'Week' : filters.period === 'month' ? 'Month' : 'Period'}</p>
                <p className="text-2xl font-bold text-gray-900">{summary.total_hours}h</p>
              </div>
              <div className="p-3 bg-teal-100 rounded-lg">
                <Clock className="w-6 h-6 text-teal-600" />
              </div>
            </div>
          </div>
          <div className="glass rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Billable</p>
                <p className="text-2xl font-bold text-green-600">{summary.billable_hours}h</p>
              </div>
              <div className="p-3 bg-green-100 rounded-lg">
                <DollarSign className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>
          <div className="glass rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Non-Billable</p>
                <p className="text-2xl font-bold text-gray-600">{summary.non_billable_hours}h</p>
              </div>
              <div className="p-3 bg-gray-100 rounded-lg">
                <Clock className="w-6 h-6 text-gray-600" />
              </div>
            </div>
          </div>
          <div className="glass rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Entries</p>
                <p className="text-2xl font-bold text-blue-600">{summary.entries_count}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-lg">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="glass rounded-xl p-4">
        <div className="flex flex-col md:flex-row gap-4 items-start md:items-center flex-wrap">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Filters:</span>
          </div>
          
          {/* Employee Filter - Super Admin Only */}
          {isSuperAdmin && (
            <Select 
              value={filters.user_id || 'all'} 
              onValueChange={(v) => setFilters({ ...filters, user_id: v === 'all' ? '' : v })}
            >
              <SelectTrigger className="w-52" data-testid="employee-filter">
                <Users className="w-4 h-4 mr-2 text-gray-500" />
                <SelectValue placeholder="All Employees" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Employees</SelectItem>
                {employees.map(emp => (
                  <SelectItem key={emp.id} value={emp.id}>
                    {emp.first_name} {emp.last_name}
                    <span className="ml-2 text-xs text-gray-400">
                      ({emp.role === 'super_admin' ? 'Super Admin' : emp.role === 'admin' ? 'Admin' : 'Employee'})
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          
          <Select value={filters.client_id || 'all'} onValueChange={(v) => setFilters({ ...filters, client_id: v === 'all' ? '' : v })}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="All Clients" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Clients</SelectItem>
              {clients.map(client => (
                <SelectItem key={client.id} value={client.id}>{client.company_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filters.period} onValueChange={(v) => setFilters({ ...filters, period: v })}>
            <SelectTrigger className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="week">This Week</SelectItem>
              <SelectItem value="month">This Month</SelectItem>
              <SelectItem value="all">All Time</SelectItem>
            </SelectContent>
          </Select>
          
          {/* Show current filter status for super admin */}
          {isSuperAdmin && filters.user_id && (
            <div className="flex items-center gap-2 bg-teal-50 text-teal-700 px-3 py-1 rounded-full text-sm">
              <Users className="w-3 h-3" />
              Viewing: {employees.find(e => e.id === filters.user_id)?.first_name || 'Selected'}'s time
              <button 
                onClick={() => setFilters({ ...filters, user_id: '' })}
                className="ml-1 hover:text-teal-900"
              >
                ×
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Super Admin: All Users View */}
      {isSuperAdmin && allUsersSummary && Object.keys(allUsersSummary).length > 0 && (
        <div className="glass rounded-xl p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Users className="w-5 h-5 mr-2 text-teal-600" />
            Team Time Summary
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(allUsersSummary).map(([userId, data]) => (
              <div key={userId} className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-gray-900">{data.user_name}</span>
                  <span className="text-sm text-gray-500">{data.entries.length} entries</span>
                </div>
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-gray-600">Total: <strong>{data.total_hours.toFixed(1)}h</strong></span>
                  <span className="text-green-600">Billable: <strong>{data.billable_hours.toFixed(1)}h</strong></span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Time Entries Table */}
      <div className="glass rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="time-entries-table">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden md:table-cell">Description</th>
                <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Duration</th>
                <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden sm:table-cell">Type</th>
                <th className="px-4 md:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {entries.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center">
                    <Clock className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                    <p className="text-gray-500">No time entries yet</p>
                    <Button
                      variant="link"
                      onClick={() => { resetForm(); setDialogOpen(true); }}
                      className="text-teal-600 mt-2"
                    >
                      Log your first entry
                    </Button>
                  </td>
                </tr>
              ) : (
                entries.map((entry, index) => (
                  <tr key={entry.id} className="hover:bg-gray-50" data-testid={`time-entry-row-${index}`}>
                    <td className="px-4 md:px-6 py-4 text-sm text-gray-600">
                      {entry.date ? new Date(entry.date).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-4 md:px-6 py-4">
                      <div className="font-medium text-gray-900 text-sm">{entry.client_name || getClientName(entry.client_id)}</div>
                      {entry.service_name && (
                        <div className="text-xs text-gray-500">{entry.service_name}</div>
                      )}
                    </td>
                    <td className="px-4 md:px-6 py-4 text-sm text-gray-600 hidden md:table-cell max-w-xs truncate">
                      {entry.description}
                    </td>
                    <td className="px-4 md:px-6 py-4">
                      <span className="font-semibold text-gray-900">
                        {formatDuration(entry.hours, entry.minutes)}
                      </span>
                      {entry.timer_running && (
                        <span className="ml-2 text-xs text-red-600 animate-pulse">Running</span>
                      )}
                    </td>
                    <td className="px-4 md:px-6 py-4 hidden sm:table-cell">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        entry.billable ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {entry.billable ? 'Billable' : 'Non-billable'}
                      </span>
                    </td>
                    <td className="px-4 md:px-6 py-4 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => {
                            setEditingEntry(entry);
                            setFormData({
                              client_id: entry.client_id,
                              description: entry.description || '',
                              hours: entry.hours || 0,
                              minutes: entry.minutes || 0,
                              billable: entry.billable !== false,
                              hourly_rate: entry.hourly_rate || '',
                              service_id: entry.service_id || '',
                              date: entry.date ? entry.date.split('T')[0] : new Date().toISOString().split('T')[0]
                            });
                            setDialogOpen(true);
                          }}>
                            <Edit2 className="w-4 h-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => handleDelete(entry.id)}
                            className="text-red-600"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create/Edit Time Entry Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <Clock className="w-5 h-5 mr-2 text-teal-600" />
              {editingEntry ? 'Edit Time Entry' : 'Log Time'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="client_id">Client *</Label>
              <Select 
                value={formData.client_id} 
                onValueChange={(v) => setFormData({ ...formData, client_id: v })}
              >
                <SelectTrigger data-testid="time-client-select">
                  <SelectValue placeholder="Select client" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map(client => (
                    <SelectItem key={client.id} value={client.id}>{client.company_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="service_id">Service (optional)</Label>
              <Select 
                value={formData.service_id || 'none'} 
                onValueChange={(v) => setFormData({ ...formData, service_id: v === 'none' ? '' : v })}
              >
                <SelectTrigger data-testid="time-service-select">
                  <SelectValue placeholder="Select service" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No specific service</SelectItem>
                  {services.map(service => (
                    <SelectItem key={service.id} value={service.id}>{service.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="What did you work on?"
                rows={2}
                data-testid="time-description"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="hours">Hours</Label>
                <Input
                  id="hours"
                  type="number"
                  min="0"
                  value={formData.hours}
                  onChange={(e) => setFormData({ ...formData, hours: e.target.value })}
                  data-testid="time-hours"
                />
              </div>
              <div>
                <Label htmlFor="minutes">Minutes</Label>
                <Input
                  id="minutes"
                  type="number"
                  min="0"
                  max="59"
                  value={formData.minutes}
                  onChange={(e) => setFormData({ ...formData, minutes: e.target.value })}
                  data-testid="time-minutes"
                />
              </div>
              <div>
                <Label htmlFor="date">Date</Label>
                <Input
                  id="date"
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  data-testid="time-date"
                />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Switch
                  id="billable"
                  checked={formData.billable}
                  onCheckedChange={(checked) => setFormData({ ...formData, billable: checked })}
                  data-testid="time-billable-switch"
                />
                <Label htmlFor="billable" className="cursor-pointer">Billable</Label>
              </div>
              {formData.billable && (
                <div className="flex items-center gap-2">
                  <Label htmlFor="hourly_rate" className="text-sm">Rate:</Label>
                  <Input
                    id="hourly_rate"
                    type="number"
                    step="0.01"
                    className="w-24"
                    placeholder="$/hr"
                    value={formData.hourly_rate}
                    onChange={(e) => setFormData({ ...formData, hourly_rate: e.target.value })}
                    data-testid="time-rate"
                  />
                </div>
              )}
            </div>

            <DialogFooter className="flex-col sm:flex-row gap-2">
              {!editingEntry && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleStartTimer}
                  disabled={!formData.client_id}
                  className="w-full sm:w-auto"
                >
                  <Play className="w-4 h-4 mr-2" />
                  Start Timer
                </Button>
              )}
              <div className="flex gap-2 w-full sm:w-auto">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white"
                  data-testid="save-time-entry"
                >
                  {editingEntry ? 'Update' : 'Save Entry'}
                </Button>
              </div>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TimeTracking;
